import { z } from 'zod'

export const AuthorSchema = z.object({
  author_name: z.string(),
  first_name: z.string().nullable().default(null),
  last_name: z.string().nullable().default(null),
  party: z.string().nullable().default(null),
  chamber: z.string().nullable().default(null),
})
export type Author = z.infer<typeof AuthorSchema>

export const StageEntrySchema = z.object({
  date: z.string(),
  description: z.string(),
  phase: z.string(),
  chamber: z.string(),
  vote_index: z.number().int().nullable().default(null),
})
export type StageEntry = z.infer<typeof StageEntrySchema>

export const VoteDetailSchema = z.object({
  legislator_name: z.string(),
  vote_choice: z.string(),
  first_name: z.string().nullable().default(null),
  last_name: z.string().nullable().default(null),
  party: z.string().nullable().default(null),
  chamber: z.string().nullable().default(null),
})
export type VoteDetail = z.infer<typeof VoteDetailSchema>

export const VoteSessionSchema = z.object({
  vote_slug: z.string().default(''),
  date: z.string(),
  session: z.string(),
  vote_type: z.string(),
  phase: z.string().default(''),
  vote_scope: z.string().default('bill'),
  result: z.string(),
  // Single source of truth for vote categorization, baked into Gold by the CLI
  // (sync categorizeVote). REQUIRED: the export always produces it and the
  // sync export.test.ts validates real gold against this schema.
  outcome: z.enum(['approved', 'rejected', 'amendment_rejected', 'tie']),
  votes_for: z.number().int(),
  votes_against: z.number().int(),
  abstentions: z.number().int(),
  source: z.string(),
  details: z.array(VoteDetailSchema),
})
export type VoteSession = z.infer<typeof VoteSessionSchema>

export const BillWithVotesSchema = z.object({
  boletin: z.string(),
  title: z.string(),
  filing_date: z.string(),
  origin_chamber: z.string(),
  status: z.string(),
  // Gold data emits '' (not null) for "no law" — consumers must treat both '' and null as absent,
  // matching the Rust frontend's `is_some_and(|l| !l.is_empty())` guard.
  law_number: z.string().nullable().default(null),
  subjects: z.array(z.string()),
  authors: z.array(AuthorSchema).default([]),
  stages: z.array(StageEntrySchema),
  vote_sessions: z.array(VoteSessionSchema),
})
export type BillWithVotes = z.infer<typeof BillWithVotesSchema>

export const RecentVotesSchema = z.object({
  votes: z.array(BillWithVotesSchema),
})
export type RecentVotes = z.infer<typeof RecentVotesSchema>

export const BillSummarySchema = z.object({
  boletin: z.string(),
  title: z.string(),
  status: z.string(),
  filing_date: z.string(),
  origin_chamber: z.string(),
  // Gold data emits '' (not null) for "no law" — consumers must treat both '' and null as absent,
  // matching the Rust frontend's `is_some_and(|l| !l.is_empty())` guard.
  law_number: z.string().nullable().default(null),
  subjects: z.array(z.string()),
  has_votes: z.boolean(),
  latest_vote_date: z.string().nullable().default(null),
  search_text: z.string().default(''),
})
export type BillSummary = z.infer<typeof BillSummarySchema>

export const BillsIndexSchema = z.object({
  bills: z.array(BillSummarySchema),
})
export type BillsIndex = z.infer<typeof BillsIndexSchema>
